/** WebSocket client — auto-reconnects with backoff, visibility-aware, heartbeat. */

import type { WSMessage } from "@/types";

type MessageHandler = (msg: WSMessage) => void;
type StatusHandler = (connected: boolean) => void;

export class RaleySocket {
  private ws: WebSocket | null = null;
  private handlers: Set<MessageHandler> = new Set();
  private statusHandlers: Set<StatusHandler> = new Set();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private url: string;
  private shouldConnect = false;
  private _connected = false;
  private retryCount = 0;
  private boundVisibility: (() => void) | null = null;
  private boundOnline: (() => void) | null = null;

  constructor(url: string) {
    this.url = url;
  }

  get connected(): boolean {
    return this._connected;
  }

  connect(): void {
    this.shouldConnect = true;
    this.retryCount = 0;
    this.doConnect();

    // Reconnect when tab becomes visible or device comes back online
    this.boundVisibility = () => {
      if (document.visibilityState === "visible" && this.shouldConnect && !this.ws) {
        this.retryCount = 0;
        this.doConnect();
      }
    };
    this.boundOnline = () => {
      if (this.shouldConnect && !this.ws) {
        this.retryCount = 0;
        this.doConnect();
      }
    };
    document.addEventListener("visibilitychange", this.boundVisibility);
    window.addEventListener("online", this.boundOnline);
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  private setConnected(value: boolean): void {
    if (this._connected !== value) {
      this._connected = value;
      this.statusHandlers.forEach((h) => h(value));
    }
  }

  disconnect(): void {
    this.shouldConnect = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setConnected(false);
    if (this.boundVisibility) {
      document.removeEventListener("visibilitychange", this.boundVisibility);
      this.boundVisibility = null;
    }
    if (this.boundOnline) {
      window.removeEventListener("online", this.boundOnline);
      this.boundOnline = null;
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private doConnect(): void {
    if (!this.shouldConnect) return;
    // Don't open a new socket if one is already connecting/open
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) return;

    try {
      this.ws = new WebSocket(this.url);
    } catch {
      this.setConnected(false);
      this.scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.retryCount = 0;
      this.setConnected(true);
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as WSMessage;
        this.handlers.forEach((h) => h(msg));
      } catch {
        // Ignore malformed messages
      }
    };

    this.ws.onclose = () => {
      this.ws = null;
      this.setConnected(false);
      this.stopHeartbeat();
      this.scheduleReconnect();
    };

    this.ws.onerror = () => {
      this.ws?.close();
    };
  }

  private scheduleReconnect(): void {
    if (!this.shouldConnect || this.reconnectTimer) return;
    // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
    const delay = Math.min(1000 * Math.pow(2, this.retryCount), 30_000);
    this.retryCount++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    // Send a ping every 25s to keep the connection alive
    this.heartbeatTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        try {
          this.ws.send("ping");
        } catch {
          // Connection likely dead — close will trigger reconnect
          this.ws?.close();
        }
      }
    }, 25_000);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }
}
